// components/notifications/NotificationPermissionPrompt.jsx
import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Check, Settings } from 'lucide-react';

const NotificationPermissionPrompt = ({ onPermissionGranted, onPermissionDenied }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = () => {
    if (!('Notification' in window)) {
      setPermissionStatus('unsupported');
      return;
    }

    const status = Notification.permission;
    setPermissionStatus(status);
    
    // Show prompt if not granted and not dismissed
    if (status === 'default' && !localStorage.getItem('notification_prompt_dismissed')) {
      // Wait a bit before showing prompt
      setTimeout(() => setShowPrompt(true), 3000);
    }
  };

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        // Show success message
        showSuccessNotification();
        onPermissionGranted?.();
        setShowPrompt(false);
        
        // Save to localStorage that user enabled
        localStorage.setItem('notifications_enabled', 'true');
      } else {
        onPermissionDenied?.();
        handleDismiss();
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  const showSuccessNotification = () => {
    // Test notification to confirm it works
    const testNotification = new Notification('✅ Notifications Enabled', {
      body: 'You will now receive real-time updates about orders, messages, and more!',
      icon: '/favicon.ico',
      silent: false
    });
    
    setTimeout(() => testNotification.close(), 3000);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('notification_prompt_dismissed', 'true');
  };

  // Don't show if already granted or unsupported
  if (permissionStatus === 'granted' || permissionStatus === 'unsupported' || dismissed) {
    return null;
  }

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-slide-up">
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-orange-500" />
            </div>
          </div>
          
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">Enable Notifications</h4>
            <p className="text-sm text-gray-600 mt-1">
              Get real-time alerts for orders, messages, and important updates
            </p>
            
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={requestPermission}
                className="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors"
              >
                Enable
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-gray-600 text-sm rounded-lg hover:bg-gray-100 transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionPrompt;